<template>
    <section class="wishes-grid band-cream">
        <div class="section-head">
            <span class="kicker">{{ copy.kicker }}</span>
            <h2>{{ copy.title }}</h2>
        </div>
        <ul class="wishes">
            <li
                v-for="(item, index) in copy.items"
                v-bind:key="item.icon"
                class="wish"
                :class="`wish--${item.scope}`"
                data-aos="fade-up"
                :data-aos-delay="(index % 4) * 80"
            >
                <svg
                    class="wish__icon"
                    viewBox="0 0 24 24"
                    width="26"
                    height="26"
                    aria-hidden="true"
                >
                    <path
                        :d="icons[item.icon]"
                        fill="currentColor"
                    />
                </svg>
                <span class="wish__tag">{{ copy.tags[item.scope] }}</span>
                <h3 class="wish__title">{{ item.title }}</h3>
                <p class="wish__text">{{ item.text }}</p>
            </li>
        </ul>
        <p class="wishes__more">{{ copy.more }}</p>
    </section>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import { getFeaturesContent } from '@/data/features'

// Material icon paths (24x24).
const ICONS = {
    body: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
    music: 'M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z',
    clothes: 'M21.6 18.2 13 11.75v-.91c1.65-.49 2.8-2.17 2.43-4.05-.26-1.31-1.3-2.4-2.61-2.7C10.54 3.57 8.5 5.3 8.5 7.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5c0 .84-.69 1.52-1.53 1.5-.54-.01-.97.45-.97.99v1.76L2.4 18.2c-.77.58-.36 1.8.6 1.8h18c.96 0 1.37-1.22.6-1.8zM6 18l6-4.5 6 4.5H6z',
    notes: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
    video: 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z',
    letter: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
    accounts: 'M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z',
    scheduled: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
}

export default {
    name: 'WishesGridSection',
    data() {
        return { icons: ICONS }
    },
    computed: {
        ...mapState(useStore, ['language']),
        copy() {
            return getFeaturesContent(this.language).wishes
        },
    },
}
</script>

<style lang="scss" scoped>
.wishes {
    list-style: none;
    margin: 0 auto;
    padding: 0;
    max-width: 1040px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.wish {
    position: relative;
    padding: 22px 20px 20px;
    border-radius: 18px;
    background: var(--bzioup-bg-color-A);
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: var(--bzioup-shadow-soft);
    text-align: left;
    transition: transform 220ms cubic-bezier(0.2, 0.8, 0.3, 1), box-shadow 220ms ease;
}

.wish:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(8, 20, 72, 0.1);
}

.wish__icon {
    display: block;
    color: var(--bzioup-color-pink);
    margin-bottom: 14px;
}

.wish__tag {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--bzioup-color-pink);
    background: var(--bzioup-color-pink-soft);
}

.wish--private .wish__tag {
    color: #fff;
    background: var(--bzioup-color-night);
}

.wish--private .wish__icon {
    color: var(--bzioup-color-night);
}

.wish__title {
    margin: 0 0 6px;
    font-size: 17px;
    font-weight: 700;
}

.wish__text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--bzioup-color-Y);
}

.wishes__more {
    margin: 26px 0 0;
    font-family: $font-display;
    font-size: 22px;
    color: var(--bzioup-color-Y);
    opacity: 0.7;
}

@media (max-width: $mq-medium) {
    .wishes {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
}

@media (max-width: $mq-small) {
    .wishes {
        grid-template-columns: 1fr;
    }
}
</style>
